import { type HotkeyCallback, type Options, useHotkeys } from 'react-hotkeys-hook'

/**
 * The one way Patchbay binds a key. Rules that hold everywhere (scope §5.0):
 *  - keys never fire inside an input, textarea, select or the editor (contenteditable);
 *  - sequences use a space in the registry ("g q") and are translated for the hook ("g>q");
 *  - every key also has a visible button: bind here, render the chord with <Kbd/> or a Tooltip.
 */
export function useKey(keys: string, callback: HotkeyCallback, options: Options = {}, deps: unknown[] = []) {
  const hook = keys.includes(' ') ? keys.split(' ').join('>') : keys
  return useHotkeys(
    hook,
    callback,
    {
      enableOnFormTags: false,
      enableOnContentEditable: false,
      preventDefault: true,
      ...options,
    },
    deps,
  )
}

/** Keys that are allowed inside inputs too (palette open, send). Only these two. */
export function useGlobalKey(keys: string, callback: HotkeyCallback, deps: unknown[] = []) {
  return useHotkeys(keys, callback, { enableOnFormTags: true, enableOnContentEditable: true, preventDefault: true }, deps)
}

/** True when the event's target is somewhere a typed key belongs to the user, not to us. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
}
